import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { showLoader, hideLoader } from 'store/uiSlice';
import { showToast } from 'store/toastSlice';
import { authService } from 'api/auth';
import { ChevronUpIcon, ChevronDownIcon } from 'components/icons';
import CustomDatePicker from 'components/datePicker/DatePicker';
import { ActionButton } from 'components/actionButton/actionButton';
import dayjs, { Dayjs } from 'dayjs';
import { SelfExclusionStatus } from 'types/auth';
import styles from './generalTab.module.scss';

interface SelfExclusionPanelProps {
  user: any;
}

export const SelfExclusionPanel: React.FC<SelfExclusionPanelProps> = ({ user }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isExclusionExpanded, setIsExclusionExpanded] = useState(false);
  const [exclusionCode, setExclusionCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [countdownString, setCountdownString] = useState('');
  const [isCooldownFinished, setIsCooldownFinished] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [exclusionEndDate, setExclusionEndDate] = useState<Dayjs | null>(null);

  const pendingSelfExclusion = user?.pendingSelfExclusion;
  const isCooldown = pendingSelfExclusion?.status === SelfExclusionStatus.COOLDOWN;
  const isAwaitingFinalization =
    pendingSelfExclusion?.status === SelfExclusionStatus.AWAITING_FINALIZATION;
  const hasPendingExclusion = !!pendingSelfExclusion;

  const fetchedFreshRef = useRef(false);

  useEffect(() => {
    if (!fetchedFreshRef.current) {
      fetchedFreshRef.current = true;
      if (hasPendingExclusion) {
        authService
          .fetchCurrentUser()
          .then((profileRes) => {
            if (profileRes?.data?.pendingSelfExclusion) {
              dispatch({
                type: 'auth/setPendingSelfExclusion',
                payload: profileRes.data.pendingSelfExclusion,
              });
            }
          })
          .catch((err) => console.error('Failed to fetch fresh self exclusion status', err));
      }
    }
  }, [hasPendingExclusion, dispatch]);

  useEffect(() => {
    if (isCooldown && pendingSelfExclusion?.cooldownEndsAt != null) {
      const endTime = new Date().getTime() + Number(pendingSelfExclusion.cooldownEndsAt);

      const updateTimer = () => {
        const now = new Date().getTime();
        const distance = endTime - now;

        if (distance <= 0) {
          setCountdownString('00:00:00');
          setIsCooldownFinished(true);
          return;
        }

        setIsCooldownFinished(false);
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCountdownString(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        );
      };

      updateTimer();
      if (endTime - new Date().getTime() <= 0) return;

      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [isCooldown, pendingSelfExclusion?.cooldownEndsAt]);

  useEffect(() => {
    if (isCooldown && isCooldownFinished) {
      let isMounted = true;
      let timeoutId: NodeJS.Timeout;
      let delay = 3000; // Start with 3 seconds

      const pollBackend = async () => {
        if (!isMounted) return;

        try {
          const profileRes = await authService.fetchCurrentUser();
          if (isMounted && profileRes?.data?.pendingSelfExclusion) {
            const newStatus = profileRes.data.pendingSelfExclusion.status;

            if (newStatus !== SelfExclusionStatus.COOLDOWN) {
              dispatch({
                type: 'auth/setPendingSelfExclusion',
                payload: profileRes.data.pendingSelfExclusion,
              });
              return; // Success, stop polling
            }
          }
        } catch (err) {
          console.error('Failed to poll profile at cooldown end', err);
        }

        // Still in COOLDOWN (or request failed), schedule next poll with backoff
        if (isMounted) {
          timeoutId = setTimeout(pollBackend, delay);
          delay = Math.min(delay * 2, 30000); // Double the delay, cap at 30 seconds
        }
      };

      pollBackend(); // Initial check

      return () => {
        isMounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isCooldown, isCooldownFinished, dispatch]);

  const handleRequestExclusion = async () => {
    try {
      setIsRequesting(true);
      const res = await authService.requestSelfExclusion();
      if (res?.data) {
        dispatch({
          type: 'auth/setPendingSelfExclusion',
          payload: res.data,
        });
      }
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.codeSent'), type: 'success' }));
    } catch (err: any) {
      const msg = err.response?.data?.message || t('settings.general.selfExclusion.toasts.requestFailed');
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setIsRequesting(false);
    }
  };

  const handleConfirmExclusion = async () => {
    if (!exclusionCode) return;
    try {
      setIsVerifying(true);
      const res = await authService.confirmSelfExclusion(exclusionCode);
      if (res?.data) {
        dispatch({
          type: 'auth/setPendingSelfExclusion',
          payload: res.data,
        });
      }
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.confirmed'), type: 'success' }));
      setExclusionCode('');
    } catch (err: any) {
      const msg = err.response?.data?.message || t('settings.general.selfExclusion.toasts.invalidCode');
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendExclusionCode = async () => {
    try {
      setIsResending(true);
      await authService.resendSelfExclusionCode();
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.codeResent'), type: 'success' }));
    } catch (err: any) {
      const msg = err.response?.data?.message || t('settings.general.selfExclusion.toasts.resendFailed');
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelExclusion = async () => {
    try {
      dispatch(showLoader({ text: t('settings.general.selfExclusion.cancelling') }));
      await authService.rejectSelfExclusion();
      const profileRes = await authService.fetchCurrentUser();
      dispatch({
        type: 'auth/setPendingSelfExclusion',
        payload: profileRes?.data?.pendingSelfExclusion || null,
      });
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.cancelled'), type: 'success' }));
      setIsCooldownFinished(false);
    } catch (err: any) {
      const msg = err.response?.data?.message || t('settings.general.selfExclusion.toasts.cancelFailed');
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleFinalizeExclusion = async () => {
    if (!exclusionEndDate) {
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.selectDate'), type: 'error' }));
      return;
    }

    const until = exclusionEndDate.toISOString();

    try {
      dispatch(showLoader({ text: t('settings.general.selfExclusion.finalizing') }));
      await authService.finalizeSelfExclusion(until);
      const profileRes = await authService.fetchCurrentUser();
      dispatch({
        type: 'auth/setPendingSelfExclusion',
        payload: profileRes?.data?.pendingSelfExclusion || null,
      });
      dispatch(showToast({ text: t('settings.general.selfExclusion.toasts.finalized'), type: 'success' }));
      setIsFinalizing(false);
      setExclusionEndDate(null);
    } catch (err: any) {
      const msg = err.response?.data?.message || t('settings.general.selfExclusion.toasts.finalizeFailed');
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <div className={styles.exclusionWrapper}>
      <div
        className={styles.exclusionHeader}
        onClick={() => setIsExclusionExpanded(!isExclusionExpanded)}
      >
        <span>{t('settings.general.selfExclusion.title')}</span>
        <span className={styles.icon}>
          {isExclusionExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </span>
      </div>
      {isExclusionExpanded && (
        <div className={styles.exclusionContent}>
          {isAwaitingFinalization ? (
            <div className={styles.cooldownState}>
              {isFinalizing ? (
                <>
                  <p>{t('settings.general.selfExclusion.selectEndDatePrompt')}</p>
                  <div style={{ marginTop: '16px', marginBottom: '8px', width: '100%' }}>
                    <CustomDatePicker
                      label={t('settings.general.selfExclusion.endDateLabel')}
                      selected={exclusionEndDate}
                      onChange={(date) => setExclusionEndDate(date)}
                      minDate={dayjs().add(24, 'hour')}
                    />
                  </div>
                  <div className={styles.verifyActions}>
                    <ActionButton onClick={handleFinalizeExclusion} className={styles.submitBtn}>
                      {t('settings.general.selfExclusion.submit')}
                    </ActionButton>
                    <ActionButton
                      onClick={() => setIsFinalizing(false)}
                      className={styles.resendBtn}
                    >
                      {t('settings.general.selfExclusion.back')}
                    </ActionButton>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    {t('settings.general.selfExclusion.cooldownEndedPrompt')}
                  </p>
                  <div className={styles.verifyActions}>
                    <ActionButton
                      onClick={() => setIsFinalizing(true)}
                      className={styles.submitBtn}
                    >
                      {t('settings.general.selfExclusion.confirm')}
                    </ActionButton>
                    <ActionButton onClick={handleCancelExclusion} className={styles.cancelBtn}>
                      {t('settings.general.selfExclusion.cancel')}
                    </ActionButton>
                  </div>
                </>
              )}
            </div>
          ) : isCooldown ? (
            <div className={styles.cooldownState}>
              {isCooldownFinished ? (
                <>
                  <p>{t('settings.general.selfExclusion.finalizingCooldown')}</p>
                  <div className={styles.timer} style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
                    {t('settings.general.selfExclusion.processing')}
                  </div>
                </>
              ) : (
                <>
                  <p>{t('settings.general.selfExclusion.inCooldown')}</p>
                  <div className={styles.timer}>{countdownString}</div>
                </>
              )}
            </div>
          ) : hasPendingExclusion ? (
            <div className={styles.verifyState}>
              <p>
                {t('settings.general.selfExclusion.enterCodePrompt')}
              </p>
              <input
                type="text"
                placeholder={t('settings.general.selfExclusion.codePlaceholder')}
                value={exclusionCode}
                onChange={(e) => setExclusionCode(e.target.value)}
                className={styles.editableInput}
              />
              <div className={styles.verifyActions}>
                <ActionButton
                  onClick={handleConfirmExclusion}
                  disabled={isVerifying || !exclusionCode}
                  className={styles.submitBtn}
                  isLoading={isVerifying}
                >
                  {t('settings.general.selfExclusion.confirm')}
                </ActionButton>
                <ActionButton
                  onClick={handleResendExclusionCode}
                  disabled={isResending}
                  isLoading={isResending}
                  className={styles.resendBtn}
                >
                  {t('settings.general.selfExclusion.resendCode')}
                </ActionButton>
              </div>
            </div>
          ) : (
            <div className={styles.idleState}>
              <p>
                {t('settings.general.selfExclusion.idleDesc')}
              </p>
              <ActionButton
                onClick={handleRequestExclusion}
                disabled={isRequesting}
                isLoading={isRequesting}
                className={styles.requestBtn}
              >
                {t('settings.general.selfExclusion.requestBtn')}
              </ActionButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
